import { useCallback, useContext, useMemo } from 'react';
import { QueryClientContext } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import type { PermissionKey, ScopeEntry } from '@nexa/auth';
import { useSessionScope } from '@/auth/session-claims';
import { ME_QUERY_KEY, type IMeAccessRole } from '@/lib/me/api';
import {
  useActiveAccessSnapshot,
  useCachedAccessSnapshot,
  type IAccessSnapshotState,
} from './snapshot';
import { NO_ACCESS, type AccessSource, type IAccessSnapshot } from './resolve';

// `useAccess()` — the operator console's permission API (users-roles-permissions R5,
// brief §6.7). Replaces `<RoleGate>` / `hasRole` / sidebar `minRole` as call sites migrate
// (R10). Backed by `GET /v1/me@v2`; everything answers `false` until access is known.

export interface IAccess {
  /** Holds this exact permission. `false` while loading, on error and for no access. */
  can(permission: PermissionKey): boolean;
  /** Holds at least one of them. */
  canAny(...permissions: PermissionKey[]): boolean;
  /** Holds every one of them (`true` for none). */
  canAll(...permissions: PermissionKey[]): boolean;
  permissions: ReadonlySet<PermissionKey>;
  roles: readonly IMeAccessRole[];
  roleKeys: readonly string[];
  /** Scope list; tenant-wide is `[{ kind: 'ALL' }]`, none is `[]`. */
  scope: readonly ScopeEntry[];
  /** Effective airports: `'*'` = the whole airline, `[]` = none. */
  airports: readonly string[] | '*';
  /** Signed in with no role assigned ("Sin acceso asignado"). */
  noAccess: boolean;
  /** Access is not known yet and is being fetched. */
  loading: boolean;
  /** A (re-)read of `/v1/me` is in flight — e.g. after `refresh()`. */
  refreshing: boolean;
  /** Why access could not be loaded — set only while it is unknown. */
  error: unknown;
  /** True once a snapshot is known (from `/v1/me` or the dev provider). */
  ready: boolean;
  accessVersion: number;
  source: AccessSource | null;
  /** Re-read `GET /v1/me` (retry after an error, "check again"). */
  refresh(): void;
}

const EMPTY_SET: ReadonlySet<PermissionKey> = new Set();

/** A 403 on `/v1/me` itself can only be NO_ACCESS_ASSIGNED — treat it as no access. */
function isForbidden(error: unknown): boolean {
  return error instanceof HTTPError && error.response.status === 403;
}

/** Shape the public API over a snapshot (exported for `<Can>` tests and stories). */
export function accessApi(
  snapshot: IAccessSnapshot | null,
  state: { loading: boolean; refreshing?: boolean; error: unknown; refresh(): void },
): IAccess {
  const permissions: ReadonlySet<PermissionKey> =
    snapshot && !snapshot.noAccess ? new Set(snapshot.permissions) : EMPTY_SET;
  const can = (p: PermissionKey): boolean => permissions.has(p);
  return {
    can,
    canAny: (...ps) => ps.some(can),
    canAll: (...ps) => ps.every(can),
    permissions,
    roles: snapshot?.roles ?? [],
    roleKeys: snapshot?.roleKeys ?? [],
    scope: snapshot?.scope ?? [],
    airports: snapshot?.airports ?? [],
    noAccess: snapshot?.noAccess ?? false,
    loading: state.loading,
    refreshing: state.refreshing ?? false,
    error: snapshot ? null : state.error,
    ready: snapshot !== null,
    accessVersion: snapshot?.accessVersion ?? 0,
    source: snapshot?.source ?? null,
    refresh: state.refresh,
  };
}

/** Shape a reader's state into the public API (shared by both readers). */
function useAccessApi(state: IAccessSnapshotState): IAccess {
  const qc = useContext(QueryClientContext);
  const refresh = useCallback(() => {
    void qc?.invalidateQueries({ queryKey: ME_QUERY_KEY });
  }, [qc]);
  const { snapshot, loading, refreshing, error } = state;

  // A 403 from `/v1/me` = NO_ACCESS_ASSIGNED from a BFF that refuses even that route.
  const effective: IAccessSnapshot | null = !snapshot && isForbidden(error) ? NO_ACCESS : snapshot;

  return useMemo(
    () => accessApi(effective, { loading, refreshing, error, refresh }),
    [effective, loading, refreshing, error, refresh],
  );
}

/** The operator's access. Fetches `GET /v1/me` once (shared cache) when signed in. */
export function useAccess(): IAccess {
  return useAccessApi(useActiveAccessSnapshot(useSessionScope()));
}

/**
 * Read access WITHOUT fetching — for components rendered everywhere (the airport scope
 * hook, `<RoleGate>`) whose tests have no BFF. Sees whatever the access boundary loaded.
 */
export function useCachedAccess(): IAccess {
  return useAccessApi(useCachedAccessSnapshot(useSessionScope()));
}
